/**
 * Webhook Utility for Zoho Flow Integration
 * Sends quiz completion data to Zoho CRM
 */

import { generateWebhookPayload } from './scoring';

// API Proxy URL - Uses Vercel serverless function to bypass CORS
const WEBHOOK_URL = '/api/webhook';

/**
 * Send quiz data to Zoho Flow webhook via API proxy
 * Uses Vercel serverless function to bypass CORS restrictions
 * @param {Object} answers - All quiz answers
 * @param {Object} leadInfo - Lead capture form data
 * @param {Object} metadata - Additional metadata (timing, UTM params, etc.)
 * @returns {Promise<boolean>} Success status
 */
export async function sendToZoho(answers, leadInfo, metadata = {}) {
  try {
    // Generate complete webhook payload
    const payload = generateWebhookPayload(answers, leadInfo, metadata);
    
    console.log('[Webhook] Sending data via API proxy:', payload);
    
    // Send POST request to our API proxy (which forwards to Zoho)
    const response = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(`API proxy failed: ${response.status} - ${result.error || 'Unknown error'}`);
    }
    
    if (!result.success) {
      throw new Error(`Webhook failed: ${result.error || 'Unknown error'}`);
    }
    
    console.log('[Webhook] Success - Data sent to Zoho Flow via proxy');
    return true;
    
  } catch (error) {
    // Log error but don't block user from seeing results
    console.error('[Webhook] Error sending to Zoho Flow:', error);
    console.error('[Webhook] Payload that failed:', generateWebhookPayload(answers, leadInfo, metadata));
    
    // Return false but application continues
    return false;
  }
}

/**
 * Send data with retry logic
 * @param {Object} answers - All quiz answers
 * @param {Object} leadInfo - Lead capture form data  
 * @param {Object} metadata - Additional metadata
 * @param {number} retries - Number of retry attempts (default: 1)
 * @returns {Promise<boolean>} Success status
 */
export async function sendToZohoWithRetry(answers, leadInfo, metadata = {}, retries = 1) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const success = await sendToZoho(answers, leadInfo, metadata);
    
    if (success) {
      return true;
    }
    
    if (attempt < retries) {
      console.log(`[Webhook] Retry attempt ${attempt + 1} of ${retries}...`);
      // Wait 2 seconds before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  }
  
  console.warn('[Webhook] All retry attempts failed. User will see results anyway.');
  return false;
}

/**
 * Extract UTM parameters from URL
 * @returns {Object} UTM parameters
 */
export function getUTMParams() {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  
  return {
    utmSource: params.get('utm_source') || '',
    utmMedium: params.get('utm_medium') || '',
    utmCampaign: params.get('utm_campaign') || '',
  };
}

export default {
  sendToZoho,
  sendToZohoWithRetry,
  getUTMParams,
};

